import { use, useEffect } from 'react'
import { EmbeddedSurfaceContext } from '@/contexts/EmbeddedSurfaceContext'

/**
 * Hook to set the document title with appropriate formatting
 * @param title The page title to display
 * @param moduleOrOptions Optional module name, or options object
 */
interface UseDocumentTitleOptions {
  module?: string
  suffix?: string
  loading?: boolean
  /** Trailing name, in place of "Scrambl" (the client portal's agency name). */
  brand?: string
  /**
   * 'layout' is a fallback: ModuleLayout's title from its `title` prop. Any
   * 'page' title (the default) on screen wins over it, whichever effect ran
   * last — the two used to overwrite each other, so a form's tab flipped
   * between "QA form · Submissions | Forms" and "QA form | Scrambl".
   */
  priority?: 'layout' | 'page'
}

interface TitleClaim {
  id: number
  text: string
  rank: number
}

const DEFAULT_TITLE = 'Scrambl'
const RANK = { layout: 0, page: 1 } as const

/** Every mounted title, oldest first; the highest rank, newest, is shown. */
const claims: TitleClaim[] = []
let nextClaimId = 0

function showWinningTitle() {
  let winner: TitleClaim | undefined
  for (const claim of claims) {
    if (!winner || claim.rank >= winner.rank) winner = claim
  }
  document.title = winner?.text ?? DEFAULT_TITLE
}

export function useDocumentTitle(
  title: string | undefined,
  moduleOrOptions?: string | UseDocumentTitleOptions,
) {
  const moduleName =
    typeof moduleOrOptions === 'string'
      ? moduleOrOptions
      : moduleOrOptions?.module || moduleOrOptions?.suffix
  const isLoading =
    typeof moduleOrOptions === 'object' && Boolean(moduleOrOptions?.loading)
  const brand =
    (typeof moduleOrOptions === 'object' && moduleOrOptions?.brand) ||
    DEFAULT_TITLE
  const rank =
    RANK[
      (typeof moduleOrOptions === 'object' && moduleOrOptions?.priority) ||
        'page'
    ]
  const isEmbedded = use(EmbeddedSurfaceContext)

  useEffect(() => {
    // Embedded surfaces share the host tab — its title belongs to the host page.
    if (isEmbedded) {
      return
    }

    // No title yet claims nothing: another title on screen (or the app name)
    // shows instead of a bare "Scrambl" over it.
    if (!isLoading && !title) {
      return
    }

    // Format: "Record Name | Scrambl" or "Record Name | Module | Scrambl"
    const text = isLoading
      ? `Loading… | ${brand}`
      : moduleName
        ? `${title} | ${moduleName} | ${brand}`
        : `${title} | ${brand}`

    const claim: TitleClaim = { id: nextClaimId++, text, rank }
    claims.push(claim)
    showWinningTitle()

    // Unmounting (or a new title) drops this claim; whatever is still on
    // screen takes the tab back, or the app name when nothing is.
    return () => {
      const index = claims.findIndex(entry => entry.id === claim.id)
      if (index !== -1) claims.splice(index, 1)
      showWinningTitle()
    }
  }, [brand, isEmbedded, isLoading, moduleName, rank, title])
}
